/**
 * ContactJourneyGraph (PR-B, Fase 4)
 *
 * 2D timeline graph of a contact's label journey rendered as SVG with
 * framer-motion-driven pan/zoom. Zero new dependencies (framer-motion is
 * already in package.json; recharts/d3/react-flow intentionally avoided).
 *
 * Visual model: ()->()---()
 *   - Each node = a label change event. Color matches the label.
 *   - Removed/assigned drawn the same; an inner glyph (+/-) disambiguates.
 *   - Edges connect consecutive events in chronological order with a
 *     direction arrow.
 *   - X axis = time. We use linear distance with a soft-cap so a 6-month
 *     gap doesn't push everything off-screen.
 *   - Y axis = stable per-label lane. Same label across time draws on the
 *     same row, so the user sees recurring patterns ("entrou e saiu 3x").
 *
 * Performance contract: render 100% via a single <g transform> on the
 * outer container so pan/zoom is one transform per frame, not N.
 * Tooltip is HTML on top of the SVG (no SVG re-paint on hover).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Loader2, AlertCircle, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import contactJourneyService from '../../services/global/contactJourneyService';

const NODE_RADIUS = 14;
const LANE_HEIGHT = 56;
const X_GAP = 90;          // base horizontal spacing between events (linear)
const X_GAP_MAX = 220;     // soft cap when there's a long real-world gap
const PADDING_X = 60;
const PADDING_Y = 40;

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;

// Soft-clip the temporal gap between two events so we don't waste real
// estate on month-long pauses. We map [0, 1day] linearly to [X_GAP, X_GAP*1.5]
// then asymptote to X_GAP_MAX.
const gapToPx = (msDelta) => {
  if (!msDelta || msDelta < 0) return X_GAP;
  const day = 86_400_000;
  if (msDelta <= day) return X_GAP + (msDelta / day) * (X_GAP * 0.5);
  // Logarithmic above 1d so the rest of the month fits.
  const days = msDelta / day;
  const log = Math.log10(days);  // 1d=0, 10d=1, 100d=2
  return Math.min(X_GAP_MAX, X_GAP * 1.5 + log * 30);
};

const ContactJourneyGraph = ({ phone, colors }) => {
  const containerRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [size, setSize] = useState({ w: 800, h: 320 });

  // Pan & zoom controlled via framer-motion values so we get
  // hardware-accelerated transforms with one transform per frame.
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);
  const transform = useTransform(
    [x, y, scale],
    ([xv, yv, sv]) => `translate(${xv}px, ${yv}px) scale(${sv})`
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    contactJourneyService.getLabelHistory(phone, 200)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || res;
        // Backend returns desc — graph reads better asc (left-to-right time).
        const asc = (data?.events || []).slice().reverse();
        setEvents(asc);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Nao foi possivel carregar a jornada.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [phone]);

  // Track container size so the SVG viewBox follows the available space.
  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setSize({ w: rect.width, h: rect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Layout: assign lanes per label_id and X positions by gap. Memoized so
  // pan/zoom don't recompute it on every frame.
  const layout = useMemo(() => {
    const lanes = new Map(); // label_id -> lane index
    const nodes = [];
    let prevTs = null;
    let cursorX = PADDING_X;

    for (const ev of events) {
      if (!lanes.has(ev.label_id)) {
        lanes.set(ev.label_id, lanes.size);
      }
      const lane = lanes.get(ev.label_id);
      const tsMs = ev.timestamp ? Date.parse(ev.timestamp) : 0;
      if (prevTs != null) {
        cursorX += gapToPx(tsMs - prevTs);
      }
      prevTs = tsMs;
      nodes.push({
        ...ev,
        x: cursorX,
        y: PADDING_Y + lane * LANE_HEIGHT,
      });
    }

    const totalWidth = cursorX + PADDING_X;
    const totalHeight = PADDING_Y * 2 + Math.max(1, lanes.size) * LANE_HEIGHT;
    return { nodes, lanes, totalWidth, totalHeight };
  }, [events]);

  const handleWheel = (e) => {
    // Zoom on wheel; pan is via drag.
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale.get() + delta));
    scale.set(next);
  };

  const resetView = () => {
    x.set(0); y.set(0); scale.set(1);
  };

  const zoomBy = (factor) => {
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale.get() * factor));
    scale.set(next);
  };

  // Find currently hovered node for the tooltip card.
  const hovered = hoveredId ? layout.nodes.find((n) => n.id === hoveredId) : null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative', width: '100%', height: '100%',
        backgroundColor: colors.bgSecondary, borderRadius: 8,
        overflow: 'hidden',
      }}
      onWheel={handleWheel}
    >
      {loading ? (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: colors.textTertiary, fontSize: 12, gap: 6,
        }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          Montando jornada...
        </div>
      ) : error ? (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#b91c1c', fontSize: 12, gap: 6,
        }}>
          <AlertCircle size={12} />
          {error}
        </div>
      ) : events.length === 0 ? (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: colors.textTertiary, fontSize: 12,
        }}>
          Sem eventos para este contato.
        </div>
      ) : (
        <>
          {/* Toolbar */}
          <div style={{
            position: 'absolute', top: 8, right: 8, zIndex: 2,
            display: 'flex', gap: 4,
            backgroundColor: colors.bgPrimary,
            border: `1px solid ${colors.borderColor}`,
            borderRadius: 8, padding: 4,
          }}>
            <button onClick={() => zoomBy(0.85)} title="Zoom out" style={iconBtn(colors)}>
              <ZoomOut size={12} />
            </button>
            <button onClick={() => zoomBy(1.18)} title="Zoom in" style={iconBtn(colors)}>
              <ZoomIn size={12} />
            </button>
            <button onClick={resetView} title="Centralizar" style={iconBtn(colors)}>
              <Maximize2 size={12} />
            </button>
          </div>

          {/* Drag area uses framer-motion drag for pan. Only one transform
              applied to the outer <g>, so children are not re-evaluated.
              touchAction:none evita scroll vertical roubar o drag em mobile. */}
          <motion.svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${size.w} ${size.h}`}
            style={{ display: 'block', cursor: 'grab', touchAction: 'none' }}
          >
            {/* Arrow marker definition — shared across all edges.
                Cor robusta: prioriza textSecondary (sempre existe no theme)
                e cai em #9ca3af se algo der errado. */}
            <defs>
              <marker
                id="journey-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
                markerUnits="strokeWidth"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={edgeColor(colors)} />
              </marker>
            </defs>

            <motion.g
              drag
              dragMomentum={false}
              whileDrag={{ cursor: 'grabbing' }}
              style={{ x, y, scale, originX: 0, originY: 0 }}
            >
              {/* Capture-area: rect transparente gigante para que o drag
                  funcione em QUALQUER ponto do svg, nao apenas sobre as
                  bolinhas. Sem isso, framer-motion so detecta pointerDown
                  onde ha conteudo desenhado dentro do <g>. */}
              <rect
                x={-20000}
                y={-20000}
                width={40000}
                height={40000}
                fill="transparent"
                style={{ pointerEvents: 'all', cursor: 'grab' }}
              />

              {/* Edges (linhas conectando eventos consecutivos). Cor solida
                  no textSecondary com fallback hex — antes usava borderColor
                  que muitas vezes nao existe no theme, deixando a linha
                  invisivel e parecendo nodes desconectados. */}
              {layout.nodes.map((n, i) => {
                if (i === 0) return null;
                const prev = layout.nodes[i - 1];
                const sameLane = prev.label_id === n.label_id;
                return (
                  <line
                    key={`edge-${n.id}`}
                    x1={prev.x}
                    y1={prev.y}
                    x2={n.x}
                    y2={n.y}
                    stroke={edgeColor(colors)}
                    strokeWidth={sameLane ? 1.5 : 2}
                    strokeOpacity={sameLane ? 0.6 : 0.9}
                    strokeDasharray={sameLane ? '5 4' : 'none'}
                    strokeLinecap="round"
                    markerEnd="url(#journey-arrow)"
                    style={{ pointerEvents: 'stroke' }}
                  />
                );
              })}

              {/* Nodes */}
              {layout.nodes.map((n) => {
                const isAssign = n.event_type === 'assigned';
                return (
                  <g
                    key={n.id}
                    onMouseEnter={() => setHoveredId(n.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle
                      cx={n.x}
                      cy={n.y}
                      r={NODE_RADIUS}
                      fill={isAssign ? n.label_color : colors.bgPrimary}
                      stroke={n.label_color}
                      strokeWidth={2}
                    />
                    {/* Glyph +/-: usa <line> em vez de <text> para garantir
                        centralizacao pixel-perfect cross-browser. O text
                        de UTF − tinha alinhamento inconsistente versus +. */}
                    {isAssign ? (
                      <>
                        {/* Plus: 2 linhas perpendiculares */}
                        <line
                          x1={n.x - 5} y1={n.y}
                          x2={n.x + 5} y2={n.y}
                          stroke="#fff" strokeWidth={2.5} strokeLinecap="round"
                          style={{ pointerEvents: 'none' }}
                        />
                        <line
                          x1={n.x} y1={n.y - 5}
                          x2={n.x} y2={n.y + 5}
                          stroke="#fff" strokeWidth={2.5} strokeLinecap="round"
                          style={{ pointerEvents: 'none' }}
                        />
                      </>
                    ) : (
                      <line
                        x1={n.x - 5} y1={n.y}
                        x2={n.x + 5} y2={n.y}
                        stroke={n.label_color}
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                  </g>
                );
              })}
            </motion.g>
          </motion.svg>

          {/* Tooltip — HTML so font rendering and accessibility tools work
              normally. Positioning uses the node's untransformed coords +
              the current pan/zoom values so it tracks the node. */}
          {hovered && (
            <Tooltip node={hovered} x={x} y={y} scale={scale} colors={colors} />
          )}
        </>
      )}
    </div>
  );
};

// ---- helpers ---------------------------------------------------------------

/**
 * Cor das edges/setas. Tenta varias chaves do theme (theme tem ora `border`,
 * ora `borderColor`, ora apenas `textSecondary`). Cai em cinza neutro como
 * fallback final — garante que a linha NUNCA fique invisivel.
 */
const edgeColor = (colors) => (
  colors?.textSecondary
  || colors?.borderColor
  || colors?.border
  || '#9ca3af'
);

const iconBtn = (colors) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 24, height: 24, borderRadius: 6,
  background: 'transparent', color: colors.textSecondary,
  border: 'none', cursor: 'pointer',
});

const Tooltip = ({ node, x, y, scale, colors }) => {
  // Tooltip ANCORADO no node: usa useTransform para que o left/top siga o
  // pan/zoom em tempo real (sem React re-render). Antes lia x.get()/y.get()
  // uma vez no render e ficava defasado quando o user arrastava.
  const tooltipLeft = useTransform(
    [x, scale],
    ([xv, sv]) => `${Math.max(8, node.x * sv + xv + 18)}px`
  );
  const tooltipTop = useTransform(
    [y, scale],
    ([yv, sv]) => `${Math.max(8, node.y * sv + yv - 12)}px`
  );

  const formatted = node.timestamp
    ? new Date(node.timestamp).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: tooltipLeft,
        top: tooltipTop,
        zIndex: 3,
        backgroundColor: colors.bgPrimary,
        border: `1px solid ${edgeColor(colors)}`,
        borderRadius: 8, padding: '8px 10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontSize: 11, color: colors.textPrimary, maxWidth: 240,
        pointerEvents: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{
          width: 8, height: 8, borderRadius: 2,
          backgroundColor: node.label_color, display: 'inline-block',
        }} />
        <strong>{node.label_name}</strong>
        {node.label_deleted && (
          <span style={{ color: colors.textTertiary, fontStyle: 'italic' }}>(removida)</span>
        )}
      </div>
      {node.pipeline_name && (
        <div style={{ color: colors.textSecondary, fontSize: 11, marginBottom: 2 }}>
          Funil: <strong>{node.pipeline_name}</strong>
        </div>
      )}
      <div style={{ color: colors.textSecondary, fontSize: 11 }}>
        {node.event_type === 'assigned' ? 'entrou' : 'saiu'} • {formatted}
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: colors.textTertiary }}>
        via {node.source}
      </div>
      {node.message_excerpt && (
        <div style={{
          marginTop: 4, fontSize: 11, fontStyle: 'italic',
          color: colors.textSecondary, borderTop: `1px solid ${edgeColor(colors)}`,
          paddingTop: 4,
        }}>
          "{node.message_excerpt}"
        </div>
      )}
    </motion.div>
  );
};

export default ContactJourneyGraph;
